// ---------------------------------------------------------------------------
// Theme — three-way switch over Tailwind dark mode (class-based).
//
//   "light"  → force light palette
//   "dark"   → force dark palette
//   "system" → follow `prefers-color-scheme`, live-updating
//
// The chosen *preference* (light/dark/system) is persisted to localStorage.
// The *resolved* mode (light or dark) is applied to <html> as a class so
// Tailwind's `dark:` variants pick it up. A small inline script in index.html
// applies the right class on first paint so we never flash the wrong theme.
// ---------------------------------------------------------------------------

export type ThemePref = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const KEY = "kms-theme";
const EVENT = "kms:theme-changed";

export function getThemePref(): ThemePref {
  if (typeof window === "undefined") return "system";
  const raw = window.localStorage.getItem(KEY);
  return raw === "light" || raw === "dark" ? raw : "system";
}

function systemPrefers(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function resolveTheme(pref: ThemePref = getThemePref()): ResolvedTheme {
  return pref === "system" ? systemPrefers() : pref;
}

/** Add/remove the `dark` class on <html> based on the resolved theme.
 *  Also sets `color-scheme` so native form controls / scrollbars switch. */
export function applyTheme(resolved: ResolvedTheme = resolveTheme()): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
}

export function setThemePref(pref: ThemePref): void {
  if (typeof window === "undefined") return;
  if (pref === "system") {
    window.localStorage.removeItem(KEY);
  } else {
    window.localStorage.setItem(KEY, pref);
  }
  applyTheme(resolveTheme(pref));
  window.dispatchEvent(new CustomEvent(EVENT));
}

/** Subscribe to theme changes — preference changes via setThemePref, AND
 *  system preference flips (for users on "system"). Returns unsubscribe. */
export function subscribeTheme(cb: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onPref    = () => cb();
  const onStorage = (e: StorageEvent) => { if (e.key === KEY) cb(); };
  const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
  const onSystem  = () => { if (getThemePref() === "system") cb(); };

  window.addEventListener(EVENT, onPref);
  window.addEventListener("storage", onStorage);
  mq?.addEventListener?.("change", onSystem);

  return () => {
    window.removeEventListener(EVENT, onPref);
    window.removeEventListener("storage", onStorage);
    mq?.removeEventListener?.("change", onSystem);
  };
}
